import {
	Data,
	LayoutDataItems,
	ResolvedRefArray,
	ResolvedVariantRelation,
	Style,
	UnitSizeValue,
	UnresolvedRefArray,
	UnresolvedVariantRelation,
	VariantRelationOnlyRefArray,
} from '@wix/thunderbolt-becky-types'
import { removeHash } from './utils'

export const getOverridesRefArrayId = (refArrayId: string) => `${refArrayId}`

export type InflationDataMap = Data['theme_data' | 'layout_data' | 'variables']
export type InflationDataItem = Style | LayoutDataItems | UnitSizeValue

const inflateItem = (item: InflationDataItem, dataQuery: string, dataMap: InflationDataMap) => {
	const inflatedItemId = `${dataQuery}-${item.id}`
	const itemOverride = dataMap[inflatedItemId] as InflationDataItem
	return (
		itemOverride || {
			...item,
			id: inflatedItemId,
		}
	)
}

const inflateVariantRelation = (
	variantRelation: ResolvedVariantRelation<InflationDataItem>,
	inflatedVariantRelationId: string,
	dataQuery: string,
	dataMap: InflationDataMap
) => ({
	...variantRelation,
	id: inflatedVariantRelationId,
	to: inflateItem(variantRelation.to, dataQuery, dataMap),
})

const inflateResolvedVariantRelation = (
	variantRelation: ResolvedVariantRelation<InflationDataItem>,
	dataQuery: string,
	dataMap: InflationDataMap
) => {
	const inflatedVariantRelationId = `${dataQuery}-${variantRelation.id}`
	const variantRelationOverride = dataMap[inflatedVariantRelationId] as ResolvedVariantRelation<InflationDataItem>
	return (
		variantRelationOverride ||
		inflateVariantRelation(variantRelation, inflatedVariantRelationId, dataQuery, dataMap)
	)
}

export const inflateResolvedRefArray = (
	resolvedRefArray: ResolvedRefArray<InflationDataItem>,
	dataQuery: string,
	dataMap: InflationDataMap
) => ({
	...resolvedRefArray,
	id: dataQuery,
	values: resolvedRefArray.values.map((value) => {
		if (value.type === 'VariantRelation') {
			return inflateResolvedVariantRelation(value, dataQuery, dataMap)
		}
		return inflateItem(value, dataQuery, dataMap)
	}),
})

export const getDataOverrides = (resolvedRefArray: ResolvedRefArray<InflationDataItem>, dataQuery: string) => {
	const dataOverrides: Record<string, UnresolvedRefArray | UnresolvedVariantRelation | InflationDataItem> = {}
	dataOverrides[dataQuery] = {
		...resolvedRefArray,
		id: dataQuery,
		values: resolvedRefArray.values.map((value) => `#${value.id}`),
	}

	resolvedRefArray.values.forEach((value) => {
		if (value.type === 'VariantRelation') {
			dataOverrides[value.id] = { ...value, to: `#${value.to.id}` }
			dataOverrides[value.to.id] = value.to
		} else {
			dataOverrides[value.id] = value
		}
	})
	return dataOverrides
}

export const resolveRefArray = (refArray: UnresolvedRefArray, dataMap: InflationDataMap) =>
	({
		...refArray,
		values: refArray.values.map((itemId) => {
			const resolvedItem = dataMap[removeHash(itemId)]
			if (resolvedItem.type !== 'VariantRelation') {
				return resolvedItem
			}
			const to = dataMap[removeHash((resolvedItem as UnresolvedVariantRelation).to)]
			return {
				...resolvedItem,
				to,
			}
		}),
	} as ResolvedRefArray<InflationDataItem>)

export const inflateWithoutPresets = (
	resolvedRefArray: ResolvedRefArray<InflationDataItem>,
	dataQuery: string,
	mergedDataMap: InflationDataMap = {}
) => {
	const inflatedAndResolvedRefArray = inflateResolvedRefArray(resolvedRefArray, dataQuery, mergedDataMap)

	const overridesRefArrayId = getOverridesRefArrayId(dataQuery)
	const overridesRefArray = mergedDataMap[overridesRefArrayId] as UnresolvedRefArray
	if (overridesRefArray && overridesRefArray.type === 'RefArray') {
		const resolvedOverridesRefArray = resolveRefArray(overridesRefArray, mergedDataMap)
		const inflatedAndResolvedOverridesRefArray = inflateResolvedRefArray(
			resolvedOverridesRefArray,
			dataQuery,
			mergedDataMap
		) as VariantRelationOnlyRefArray<InflationDataItem>

		inflatedAndResolvedRefArray.values = mergeRefArrayOverrides(
			inflatedAndResolvedRefArray.values,
			inflatedAndResolvedOverridesRefArray.values.filter((item) => item.type === 'VariantRelation')
		)
	}

	return getDataOverrides(inflatedAndResolvedRefArray, dataQuery)
}

const compareArrays = (arr1: Array<string> = [], arr2: Array<string> = []) =>
	arr1.length === arr2.length && arr1.every((value) => arr2.includes(value))

// When merging overrides ref array into ref array we need to filter out duplicate variant relations.
// Duplicate variant relation is 2 variant relation with the same variants array.
export const mergeRefArrayOverrides = (
	values: ResolvedRefArray<InflationDataItem>['values'],
	overrideValues: VariantRelationOnlyRefArray<InflationDataItem>['values']
) => {
	const filteredValues = values.filter(
		(value) =>
			value.type !== 'VariantRelation' ||
			!overrideValues.find((overrideValue) => compareArrays(overrideValue.variants, value.variants))
	)
	return [...filteredValues, ...overrideValues]
}
